'use client'

import {
  inputBase,
  inputDisabled,
  inputFocus,
  inputOutlined,
} from '@/core/constants/class-names'
import { cn } from '@/core/lib/utils/cn'
import { ChangeEventHandler, InputHTMLAttributes, useState } from 'react'
import { createInput } from './input-factory'

type Props = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'onError'
> & {
  onChange?: (value: number) => void
  onError?: (err: 'exceedMax' | 'belowMin') => void
  value?: number
  className?: string
  min?: number
  max?: number
  ref?: React.Ref<HTMLInputElement>
  defaultValue?: number
}

const BaseNumberInput: React.FC<Props> = ({
  className,
  onChange,
  onError,
  min = -Infinity,
  max = Infinity,
  disabled,
  ref,
  value: inputValue,
  ...rest
}) => {
  const [value, setValue] = useState<number>(inputValue ?? 0)

  const increment = () => {
    if (value >= max) {
      return onError?.('exceedMax')
    }
    setValue(prevValue => prevValue + 1)
    onChange?.(value + 1)
  }

  const decrement = () => {
    if (value <= min) {
      return onError?.('belowMin')
    }
    setValue(prevValue => prevValue - 1)
    onChange?.(value - 1)
  }
  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    const newValue = parseInt(e.target.value) || 0
    if (newValue < min || newValue > max) {
      return
    }
    setValue(newValue)
    onChange?.(newValue)
  }

  return (
    <div className='relative flex items-center'>
      <input
        {...rest}
        disabled={disabled || (max === 0 && min === 0)}
        ref={ref}
        type='text'
        className={cn(
          'text-grey text-center',
          inputBase,
          inputFocus,
          inputOutlined,
          inputDisabled,
          className,
        )}
        value={value}
        onChange={handleChange}
      />
      <button
        className='text-grey absolute top-1/2 left-3 h-10 w-10 -translate-y-1/2 cursor-pointer text-lg'
        onClick={decrement}
        type='button'
        aria-label={`Decrement the number of ${rest.name}`}
      >
        -
      </button>
      <button
        className='text-grey absolute top-1/2 right-3 h-10 w-10 -translate-y-1/2 cursor-pointer text-lg'
        onClick={increment}
        type='button'
        aria-label={`Increment the number of ${rest.name}`}
      >
        +
      </button>
    </div>
  )
}

export const NumberInput = createInput(BaseNumberInput, ['label'])
export const FormNumberInput = createInput(BaseNumberInput, ['form', 'label'])
